<script>
import Vue from 'vue';
import store from '@/store';
import i18n from '@/services/i18n';

import nunito from '!!raw-loader!@/assets/fonts/nunito.css';
import donationFeedCss from '!!raw-loader!sass-loader!./donation-feed.scss';
import css from '!!raw-loader!sass-loader!@/assets/css/checkout/index.scss';
var MutationObserver = window.MutationObserver || window.WebKitMutationObserver;

export default {
  data() {
    return {
      mutationObserver: null,
      resizeObserver: null,
      resizeTimeout: null,
      isResizing: false
    };
  },

  render(h) {
    return h('iframe', {
      on: { load: this.renderChildren },
      attrs: {
        src: 'about:blank',
        frameborder: 0,
        title: 'Donation Feed',
        width: '100%',
        scrolling: 'no', // Prevent scrollbars
        'allow-same-origin': ''
      },
      style: {
        width: '100%',
        maxWidth: '1200px',
        height: '500px', // Initial height for donation feed
        border: 'none',
        overflow: 'hidden',
        display: 'block', // Prevent inline gaps
        transition: 'height 0.3s ease-out' // Smooth height transitions
      }
    });
  },

  watch: {
    css(newValue, oldValue) {
      if (!oldValue && newValue) {
        this.applyCss();
      }
    }
  },

  methods: {
    renderChildren() {
      const children = this.$slots.default;
      const body = this.$el.contentDocument.body;
      const div = document.createElement('DIV');

      // Ensure body has no margin/padding that could affect measurements
      body.style.margin = '0';
      body.style.padding = '0';
      body.style.overflow = 'hidden';

      body.appendChild(div);

      const DonationFeedIframe = new Vue({
        name: 'DonationFeedIframe',
        store,
        i18n,
        data: { children: Object.freeze(children) },
        render(h) {
          return h('div', this.children);
        }
      });

      DonationFeedIframe.$mount(div);
      this.DonationFeedIframe = DonationFeedIframe;

      this.applyCss();

      // Set up observers after content is rendered
      this.$nextTick(() => {
        this.setupObservers();
        this.setIframeHeight(true); // Mark as initial render
      });
    },

    applyCss() {
      const iframe = this.$el.contentDocument;
      const styleElement = document.createElement('style');
      styleElement.textContent = css + donationFeedCss + nunito;
      iframe.head.appendChild(styleElement);

      // Wait for styles to apply before measuring
      setTimeout(() => this.setIframeHeight(), 50);
    },

    setIframeHeight() {
      if (this.isResizing) return; // Prevent recursive calls

      // Clear any pending resize
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      this.resizeTimeout = setTimeout(() => {
        this.isResizing = true;
        this.performResize();
        this.isResizing = false;
      }, 50); // Debounce resizes
    },

    performResize() {
      const iframe = this.$el.contentDocument;
      if (!iframe || !iframe.body) return;

      // Save parent page scroll position
      const parentScrollY = window.parent.pageYOffset || window.parent.scrollY;
      const parentScrollX = window.parent.pageXOffset || window.parent.scrollX;

      // Save current height to prevent flicker
      const currentHeight = this.$el.style.height;
      
      // Get the actual content height WITHOUT changing iframe height
      iframe.body.style.overflow = 'hidden';
      const scrollHeight = iframe.body.scrollHeight;
      const clientHeight = iframe.body.clientHeight;
      const offsetHeight = iframe.body.offsetHeight;

      // Use the maximum of all height measurements
      const contentHeight = Math.max(scrollHeight, clientHeight, offsetHeight);
      const newHeight = Math.max(contentHeight, 200); // Minimum height for donation feed

      // Only update if height actually changed to avoid unnecessary operations
      const currentHeightValue = parseInt(currentHeight) || 0;
      if (Math.abs(newHeight - currentHeightValue) < 5) {
        return; // Skip if difference is less than 5px
      }

      // Wait for next frame to set new height smoothly
      requestAnimationFrame(() => {
        // Set the new height
        this.$el.style.height = newHeight + 'px';
        this.$el.height = newHeight + 'px';

        // Immediately restore scroll position (same frame)
        try {
          window.parent.scrollTo(parentScrollX, parentScrollY);
        } catch (e) {
          // Cross-origin restriction, ignore
        }

        // Emit resize event for parent components
        this.$emit('resize', newHeight);
      });
    },

    setupObservers() {
      this.cleanupObservers(); // Clean up any existing observers

      const target = this.$el.contentDocument.body;
      if (!target) return;

      // Mutation Observer for DOM changes
      if (MutationObserver) {
        this.mutationObserver = new MutationObserver((mutations) => {
          // Check if any mutations actually affect layout
          const shouldResize = mutations.some((mutation) => {
            // Check for added/removed nodes
            if (mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0) {
              return true;
            }
            // Check for attribute changes that might affect size
            if (mutation.type === 'attributes') {
              const sizeAttributes = ['class', 'style', 'width', 'height'];
              return sizeAttributes.includes(mutation.attributeName);
            }
            return false;
          });

          if (shouldResize) {
            this.setIframeHeight();
          }
        });

        this.mutationObserver.observe(target, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style', 'width', 'height']
        });
      }

      // ResizeObserver for element size changes (if available)
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => {
          this.setIframeHeight();
        });
        this.resizeObserver.observe(target);
      }

      // Also observe the main content div if it exists
      const contentDiv = target.querySelector('div');
      if (contentDiv && this.resizeObserver) {
        this.resizeObserver.observe(contentDiv);
      }
    },

    cleanupObservers() {
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
        this.mutationObserver = null;
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = null;
      }
    }
  },

  beforeUpdate() {
    if (!this.DonationFeedIframe) {
      return;
    }
    this.DonationFeedIframe.children = Object.freeze(this.$slots.default);

    // Trigger resize check after update
    this.$nextTick(() => {
      this.setIframeHeight();
    });
  },

  mounted() {
    // Initial setup will be done in renderChildren after iframe loads
  },

  beforeDestroy() {
    this.cleanupObservers();
    if (this.DonationFeedIframe) {
      this.DonationFeedIframe.$destroy();
    }
  }
};
</script>
